const API_BASE_URL = '/api';

class ProfileService {
  getAuthHeader() {
    const token = localStorage.getItem('authToken');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  /**
   * Get current user's profile with preferences
   */
  async getProfile() {
    const response = await fetch(`${API_BASE_URL}/profile`, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch profile');
    }

    return response.json();
  }

  /**
   * Get current user's preferences only
   */
  async getPreferences() {
    const response = await fetch(`${API_BASE_URL}/profile/preferences`, {
      method: 'GET',
      headers: {
        ...this.getAuthHeader(),
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch preferences');
    }

    return response.json();
  }

  /** First-login tour state: {seen, seenAt}. */
  async getTourState() {
    const response = await fetch(`${API_BASE_URL}/profile/tour`, {
      method: 'GET',
      headers: { ...this.getAuthHeader(), 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error('Failed to fetch tour state');
    return response.json();
  }

  /** Mark the tour seen (finished or dismissed), or reset it with seen=false. */
  async setTourSeen(seen = true) {
    const response = await fetch(`${API_BASE_URL}/profile/tour`, {
      method: 'PUT',
      headers: { ...this.getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ seen }),
    });
    if (!response.ok) throw new Error('Failed to save tour state');
    return response.json();
  }

  /**
   * Update user preferences
   */
  async updatePreferences(preferences) {
    const response = await fetch(`${API_BASE_URL}/profile/preferences`, {
      method: 'PUT',
      headers: {
        ...this.getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(preferences),
    });

    if (!response.ok) {
      throw new Error('Failed to update preferences');
    }

    return response.json();
  }

  /**
   * Set the caller's OWN display time zone (an explicit personal choice), or
   * pass '' to reset to the organization default. Returns the full profile
   * with the re-resolved zone and its source.
   */
  async updateTimezone(timezone) {
    const response = await fetch(`${API_BASE_URL}/profile/timezone`, {
      method: 'PUT',
      headers: {
        ...this.getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ timezone: timezone || '' }),
    });

    if (!response.ok) {
      let message = 'Failed to update time zone';
      try {
        const body = await response.json();
        if (body && body.error) message = body.error;
      } catch (_) { /* keep the generic message */ }
      throw new Error(message);
    }

    return response.json();
  }

  /**
   * Update theme preference (convenience method)
   */
  async updateTheme(theme) {
    const response = await fetch(`${API_BASE_URL}/profile/preferences/theme`, {
      method: 'PUT',
      headers: {
        ...this.getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ theme }),
    });

    if (!response.ok) {
      throw new Error('Failed to update theme');
    }

    return response.json();
  }

  /**
   * Update email
   */
  async updateEmail(email) {
    const response = await fetch(`${API_BASE_URL}/profile/email`, {
      method: 'PUT',
      headers: {
        ...this.getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      throw new Error('Failed to update email');
    }

    return response.json();
  }

  /**
   * Change password
   */
  async changePassword(currentPassword, newPassword, confirmPassword) {
    const response = await fetch(`${API_BASE_URL}/profile/password`, {
      method: 'PUT',
      headers: {
        ...this.getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to change password');
    }

    return data;
  }

  /**
   * "Sign out everywhere" for the current user. Ends every other session; the
   * response carries a replacement token for this browser.
   */
  async revokeMySessions() {
    const response = await fetch(`${API_BASE_URL}/profile/sessions/revoke`, {
      method: 'POST',
      headers: { ...this.getAuthHeader(), 'Content-Type': 'application/json' },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || 'Failed to sign out other sessions');
    }
    return data;
  }

  // ── VerOps Pulse personal key + devices ──────────────────────────────────

  /** Metadata for the current user's Pulse key (never the raw key). */
  async getPulseKey() {
    const response = await fetch(`${API_BASE_URL}/profile/pulse-key`, {
      method: 'GET',
      headers: { ...this.getAuthHeader(), 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error('Failed to fetch Pulse key');
    }
    return response.json();
  }

  /** Generate or regenerate the personal Pulse key. Returns the raw key once. */
  async regeneratePulseKey() {
    const response = await fetch(`${API_BASE_URL}/profile/pulse-key`, {
      method: 'POST',
      headers: { ...this.getAuthHeader(), 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error('Failed to generate Pulse key');
    }
    return response.json();
  }

  /** Revoke the personal Pulse key (and all its devices). */
  async revokePulseKey() {
    const response = await fetch(`${API_BASE_URL}/profile/pulse-key`, {
      method: 'DELETE',
      headers: { ...this.getAuthHeader() },
    });
    if (!response.ok) {
      throw new Error('Failed to revoke Pulse key');
    }
  }

  /** List devices connected with the Pulse key. */
  async getPulseDevices() {
    const response = await fetch(`${API_BASE_URL}/profile/pulse-devices`, {
      method: 'GET',
      headers: { ...this.getAuthHeader(), 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error('Failed to fetch Pulse devices');
    }
    return response.json();
  }

  /** Revoke a single connected device. */
  async revokePulseDevice(deviceId) {
    const response = await fetch(`${API_BASE_URL}/profile/pulse-devices/${deviceId}`, {
      method: 'DELETE',
      headers: { ...this.getAuthHeader() },
    });
    if (!response.ok) {
      throw new Error('Failed to revoke device');
    }
  }
}

const profileService = new ProfileService();
export default profileService;
